import {useCallback, useEffect, useMemo, useState} from "react"
import {
    INSTANCE_LOADING_STATE,
    PayPalGuestPaymentButton,
    PayPalProvider,
    useEligibleMethods,
    usePayPal
} from "@paypal/react-paypal-js/sdk-v6"
import FormError from "../../components/elements/FormError"
import PaypalMethodList from "../../components/elements/paypal/PaypalMethodList"
import PaypalCardSection from "../../components/elements/paypal/PaypalCardSection"
import PaypalWalletSection from "../../components/elements/paypal/PaypalWalletSection"
import PaypalPayLaterPanel from "../../components/elements/paypal/PaypalPayLaterPanel"
import {ADVANCED_CARDS, buildEligibleMethods, PaymentMethod} from "../../components/elements/paypal/paypalMethods"
import SuccessModal from "../SuccessModal"
import {getCallbackUrlWithParameters, usePayment} from "../../contexts/PaymentContext"
import {useMerchant} from "../../contexts/MerchantContext"
import {capturePaypalOrder, createPaypalOrder, getPaypalClientId} from "../../services/PaypalService"
import "../../styles/paypal.css"

// Every way to pay this page can offer. PayPal only hands back the ones the merchant, buyer and
// currency are actually eligible for, so loading a component here does not mean it gets shown.
const PAYPAL_COMPONENTS = [
    "paypal-payments",
    "paypal-guest-payments",
    "venmo-payments",
    "card-fields",
    "googlepay-payments",
    "applepay-payments"
]

// Google Pay and Apple Pay put the merchant's country in the payment request. PayPal orders from
// this page are USD-only today (see CHANNEL_CURRENCY_SUPPORT), so the currency identifies it.
const COUNTRY_BY_CURRENCY = {USD: "US", NGN: "NG", GBP: "GB", EUR: "DE", CAD: "CA", AUD: "AU"}

function getPaypalEnvironment() {
    const configured = (window.env?.PAYPAL_ENVIRONMENT || "sandbox").toLowerCase()
    return configured === "live" || configured === "production" ? "production" : "sandbox"
}

function formatCurrency(value, currencyCode) {
    const amount = Number(value)
    if (!Number.isFinite(amount)) return ""

    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currencyCode,
            currencyDisplay: "narrowSymbol"
        }).format(amount)
    } catch (error) {
        // Unknown currency code, or a browser without narrowSymbol support.
        return `${amount.toFixed(2)} ${currencyCode}`
    }
}

const PayWithPaypal = () => {
    const environment = useMemo(getPaypalEnvironment, [])
    const [clientId, setClientId] = useState(undefined)
    const [clientIdError, setClientIdError] = useState("")

    // The SDK cannot boot without a client id, and PayPalProvider defers loading until this
    // resolves — hence fetching it into state rather than handing the provider a bare promise.
    useEffect(() => {
        let cancelled = false

        getPaypalClientId()
            .then((id) => {
                if (!cancelled) setClientId(id)
            })
            .catch(() => {
                if (!cancelled) setClientIdError("PayPal is currently unavailable. Please try another payment method.")
            })

        return () => {
            cancelled = true
        }
    }, [])

    if (clientIdError) {
        return (
            <div className="box">
                <FormError>{clientIdError}</FormError>
            </div>
        )
    }

    return (
        <div className="box">
            <PayPalProvider
                clientId={clientId}
                environment={environment}
                components={PAYPAL_COMPONENTS}
                pageType="checkout"
            >
                <PaypalCheckout environment={environment}/>
            </PayPalProvider>
        </div>
    )
}

const PaypalCheckout = ({environment}) => {
    const {paymentParams} = usePayment()
    const {merchantDetails, getFeeDetailsByChannel} = useMerchant()
    const {loadingStatus, error: sdkError, eligiblePaymentMethods} = usePayPal()

    const currencyCode = paymentParams.currency || "USD"
    const feeDetails = getFeeDetailsByChannel("PAYPAL")
    const orderTotal = Number(feeDetails.totalFloat || paymentParams.amount || 0).toFixed(2)

    const {isLoading: eligibilityLoading, error: eligibilityError} = useEligibleMethods({
        payload: {currencyCode, amount: orderTotal, paymentFlow: "ONE_TIME_PAYMENT"}
    })

    const [selectedMethod, setSelectedMethod] = useState(null)
    const [errorMessage, setErrorMessage] = useState("")
    const [capturedTransaction, setCapturedTransaction] = useState(null)

    const formatAmount = useCallback((value) => formatCurrency(value, currencyCode), [currencyCode])

    const methods = useMemo(() => buildEligibleMethods(eligiblePaymentMethods), [eligiblePaymentMethods])

    // Resolved during render rather than in an effect, so there is never a pass where eligibility
    // has arrived but nothing is selected yet. Defaults to PayPal itself when it is on offer — as
    // in the design — and otherwise to whatever sits at the top of the list.
    const selected = useMemo(() => {
        const chosen = methods.find((method) => method.id === selectedMethod)
        if (chosen) return chosen

        return methods.find((method) => method.id === PaymentMethod.PAYPAL) || methods[0]
    }, [methods, selectedMethod])

    const createOrder = useCallback(() => createPaypalOrder({
        referenceNumber: paymentParams.payment_reference,
        pagaAccountId: merchantDetails.accountId,
        amount: paymentParams.amount,
        currencyCode,
        customId: paymentParams.payment_reference,
        returnUrl: paymentParams.charge_url ? getCallbackUrlWithParameters(paymentParams, "success") : "",
        cancelUrl: paymentParams.charge_url ? getCallbackUrlWithParameters(paymentParams, "failed") : ""
    }), [paymentParams, merchantDetails.accountId, currencyCode])

    const handleApprove = useCallback(async (orderId) => {
        setErrorMessage("")
        try {
            setCapturedTransaction(await capturePaypalOrder(orderId))
        } catch (error) {
            setErrorMessage(error.message || "Payment could not be completed, please try again.")
        }
    }, [])

    const handleCancel = useCallback(() => {
        setErrorMessage("Payment canceled. Please try again.")
    }, [])

    if (sdkError || eligibilityError) {
        return <FormError>PayPal is currently unavailable. Please try another payment method.</FormError>
    }

    if (loadingStatus === INSTANCE_LOADING_STATE.PENDING || eligibilityLoading) {
        return (
            <div className="pp-loading">
                <div className="pp-spinner"></div>
                <div className="pp-loading-text">Loading payment options…</div>
            </div>
        )
    }

    if (!methods.length) {
        return <FormError>No PayPal payment option is available for this payment.</FormError>
    }

    const isPayLater = selected.id === PaymentMethod.PAY_LATER
    // Card is only collected on this page when the merchant is eligible for advanced card
    // fields; otherwise the row itself carries PayPal's guest checkout button and form.
    const isCard = selected.id === PaymentMethod.CARD
    const isEmbeddedCard = isCard && selected.fundingSource === ADVANCED_CARDS
    const isGuestCard = isCard && !isEmbeddedCard
    const countryCode = COUNTRY_BY_CURRENCY[currencyCode] || "US"
    const amount = {value: orderTotal, currencyCode}

    return (
        <div className="pp-page">
            <div>
                <div className="pp-total">
                    <span className="pp-total-label">Order total</span>
                    <span className="pp-total-amount">{formatAmount(orderTotal)}</span>
                </div>

                <div className="pp-section-label">Payment</div>

                <PaypalMethodList
                    methods={methods}
                    selected={selected.id}
                    onSelect={setSelectedMethod}
                    selectedAction={isGuestCard ? (
                        <PayPalGuestPaymentButton
                            createOrder={createOrder}
                            onApprove={(data) => handleApprove(data.orderId)}
                            onCancel={handleCancel}
                            onError={(error) => setErrorMessage(
                                error?.message || 'Sorry, your payment could not be processed. Please try again.'
                            )}
                        />
                    ) : null}
                />

                {errorMessage ? <FormError className="mt-3">{errorMessage}</FormError> : null}

                {isPayLater ? <PaypalPayLaterPanel amount={orderTotal} formatAmount={formatAmount}/> : null}
            </div>

            {isEmbeddedCard ? (
                <PaypalCardSection
                    amount={amount}
                    payLabel={`Pay ${formatAmount(orderTotal)}`}
                    footnote={`Includes fee ${formatAmount(feeDetails.amount || 0)}`}
                    createOrder={createOrder}
                    onApprove={handleApprove}
                    onFailure={setErrorMessage}
                />
            ) : isGuestCard ? (
                // The guest button lives in the card row with the form it opens, so the footer
                // is only the note explaining where the card details go.
                <div className="pp-footer">
                    <div className="pp-footnote">{getFootnote(selected.id, selected.label)}</div>
                </div>
            ) : (
                <div className="pp-footer">
                    <div className="pp-cta-slot">
                        <PaypalWalletSection
                            method={selected.id}
                            amount={amount}
                            countryCode={countryCode}
                            merchantName={merchantDetails.displayName || merchantDetails.name}
                            environment={environment}
                            googlePayConfig={selected.id === PaymentMethod.GOOGLE_PAY
                                ? eligiblePaymentMethods?.getDetails("googlepay")?.config
                                : undefined}
                            applePayConfig={selected.id === PaymentMethod.APPLE_PAY
                                ? eligiblePaymentMethods?.getDetails("applepay")?.config
                                : undefined}
                            createOrder={createOrder}
                            onApprove={handleApprove}
                            onCancel={handleCancel}
                            onFailure={setErrorMessage}
                        />
                    </div>
                    <div className="pp-footnote">{getFootnote(selected.id, selected.label)}</div>
                </div>
            )}

            <SuccessModal show={!!capturedTransaction} onHide={() => {}}
                          paymentDetails={{
                              refNo: paymentParams.payment_reference,
                              transactionType: selected?.label || "PayPal",
                              amount: paymentParams.amount,
                              fee: feeDetails.amount,
                              tax: feeDetails.taxes ? feeDetails.taxes.vat : null,
                              total: feeDetails.total
                          }}/>
        </div>
    )
}

function getFootnote(method, label) {
    switch (method) {
        case PaymentMethod.CARD:
            return "Your card details are entered securely with PayPal"
        case PaymentMethod.PAY_LATER:
            return "No interest, no late fees"
        case PaymentMethod.GOOGLE_PAY:
        case PaymentMethod.APPLE_PAY:
            return `Confirm this payment in the ${label} sheet`
        default:
            return `You will be redirected to ${label}`
    }
}

export default PayWithPaypal
